import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Mail, Calendar } from 'lucide-react';

const serviceLinks = [
  { label: 'Individual Tax Return', to: '/services/individual-tax' },
  { label: 'Business Tax Return', to: '/services/business-tax' },
  { label: 'Business Structuring', to: '/services/business-structuring' },
  { label: 'Business Activity Statement (BAS)', to: '/services/bas' },
  { label: 'ASIC Lodgments', to: '/services/asic' },
  { label: 'Bookkeeping', to: '/services/bookkeeping' },
  { label: 'Software Training', to: '/services/software-training' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <style>{`
        .topbar {
          background: var(--charcoal-dark);
          padding: 9px 0;
          transition: all 0.3s ease;
        }
        .topbar-inner {
          display: flex; align-items: center;
          justify-content: space-between;
        }
        .topbar-left {
          display: flex; align-items: center; gap: 24px;
        }
        .topbar-link {
          display: flex; align-items: center; gap: 7px;
          font-size: 12px; font-weight: 400; color: rgba(255,255,255,0.65);
          transition: color 0.2s;
        }
        .topbar-link:hover { color: var(--gold-hover); }
        .topbar-link svg { color: var(--gold); flex-shrink: 0; }
        .topbar-right { display: flex; align-items: center; gap: 0; }
        .topbar-nav-link {
          font-size: 12px; font-weight: 400; color: rgba(255,255,255,0.55);
          padding: 0 16px; transition: color 0.2s; border-right: 1px solid rgba(255,255,255,0.1);
          line-height: 1;
        }
        .topbar-nav-link:last-child { border-right: none; }
        .topbar-nav-link:hover { color: var(--gold-hover); }

        /* ── Main Nav ── */
        .navbar {
          background: var(--white);
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 200;
          box-shadow: ${scrolled ? 'var(--shadow)' : 'none'};
          transition: box-shadow 0.3s ease;
        }
        .navbar-inner {
          display: flex; align-items: center;
          justify-content: space-between;
          height: 76px;
        }
        .nav-logo { display: flex; align-items: center; }
        .nav-logo img { height: 46px; width: auto; object-fit: contain; }

        .nav-menu { display: flex; align-items: center; height: 76px; }
        .nav-item { position: relative; height: 100%; }
        .nav-link {
          display: flex; align-items: center; gap: 5px;
          height: 100%; padding: 0 18px;
          font-size: 14px; font-weight: 500;
          color: var(--grey-700);
          border-bottom: 3px solid transparent;
          transition: color 0.2s, border-color 0.2s;
          white-space: nowrap; cursor: pointer;
        }
        .nav-link:hover, .nav-link.active {
          color: var(--charcoal-dark);
          border-bottom-color: var(--gold);
        }
        .nav-link svg { transition: transform 0.25s ease; }
        .nav-item:hover .nav-link svg { transform: rotate(180deg); }

        /* Dropdown */
        .nav-dropdown {
          position: absolute; top: calc(100% + 0px); left: 0;
          background: var(--white); min-width: 280px;
          border-top: 3px solid var(--gold);
          border: 1px solid var(--border);
          border-top: 3px solid var(--gold);
          border-radius: 0 0 var(--radius-sm) var(--radius-sm);
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          opacity: 0; visibility: hidden;
          transform: translateY(-6px);
          transition: all 0.25s ease;
          z-index: 300;
        }
        .nav-item:hover .nav-dropdown {
          opacity: 1; visibility: visible; transform: translateY(0);
        }
        .nav-dropdown a {
          display: block; padding: 12px 22px;
          font-size: 13px; font-weight: 400;
          color: var(--text-body);
          border-left: 3px solid transparent;
          transition: all 0.2s ease;
        }
        .nav-dropdown a:hover {
          color: var(--charcoal-dark); background: var(--grey-100);
          border-left-color: var(--gold); padding-left: 28px;
        }
        .nav-dropdown a + a { border-top: 1px solid var(--border-light); }

        /* CTA */
        .nav-phone {
          display: flex; align-items: center; gap: 8px;
          margin-left: 16px; padding: 0 16px;
          font-size: 15px; font-weight: 600; color: var(--charcoal-dark);
          border-left: 1px solid var(--border);
          white-space: nowrap;
        }
        .nav-phone svg { color: var(--gold); }
        .nav-cta-btn {
          margin-left: 16px;
          padding: 11px 24px;
          background: var(--gold); color: var(--charcoal-dark);
          font-size: 13px; font-weight: 700; letter-spacing: 0.3px;
          transition: var(--transition); white-space: nowrap;
          border: 2px solid var(--gold);
          border-radius: var(--radius-sm);
        }
        .nav-cta-btn:hover { background: var(--gold-hover); border-color: var(--gold-hover); transform: translateY(-1px); }

        /* Mobile */
        .mobile-btn { display: none; color: var(--charcoal-dark); padding: 6px; }
        .mobile-drawer {
          display: none; position: fixed; top: 0; right: -100%;
          width: min(360px, 100vw); height: 100vh;
          background: var(--white); z-index: 500;
          flex-direction: column; overflow-y: auto;
          box-shadow: var(--shadow-lg);
          border-radius: var(--radius-lg) 0 0 var(--radius-lg);
          transition: right 0.35s cubic-bezier(0.4,0,0.2,1);
        }
        .mobile-drawer { display: flex; }
        .mobile-drawer.open { right: 0; }
        .mobile-overlay {
          display: none; position: fixed; inset: 0;
          background: rgba(0,0,0,0.5); z-index: 499;
        }
        .mobile-overlay.open { display: block; }
        .drawer-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 24px; border-bottom: 1px solid var(--border);
        }
        .drawer-header img { height: 38px; }
        .drawer-close { color: var(--grey-700); padding: 4px; }
        .drawer-body { padding: 20px 24px; flex: 1; }
        .mob-nav-link {
          display: block; padding: 14px 0;
          font-size: 15px; font-weight: 500; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light);
          transition: color 0.2s;
        }
        .mob-nav-link:hover, .mob-nav-link.active { color: var(--gold); }
        .mob-services-toggle {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 0; font-size: 15px; font-weight: 500; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light); cursor: pointer;
          background: none; border-left: none; border-right: none; border-top: none;
          width: 100%; text-align: left; font-family: var(--font-body);
        }
        .mob-sub { padding-left: 16px; overflow: hidden; max-height: 0; transition: max-height 0.35s ease; }
        .mob-sub.open { max-height: 400px; }
        .mob-sub a {
          display: block; padding: 10px 0;
          font-size: 14px; color: var(--text-body);
          border-bottom: 1px solid var(--border-light);
        }
        .mob-sub a:hover { color: var(--gold); }
        .drawer-footer {
          padding: 24px; border-top: 1px solid var(--border);
          display: flex; flex-direction: column; gap: 12px;
        }
        .mob-phone-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 13px; background: var(--charcoal-dark); color: white;
          font-size: 14px; font-weight: 600; border-radius: var(--radius-sm);
        }
        .mob-book-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 13px; background: var(--gold); color: var(--charcoal-dark);
          font-size: 14px; font-weight: 600; border-radius: var(--radius-sm);
        }

        @media (max-width: 1060px) {
          .nav-menu { display: none; }
          .nav-phone { display: none; }
          .nav-cta-btn { display: none; }
          .mobile-btn { display: block; }
        }
        @media (max-width: 600px) {
          .topbar-right { display: none; }
          .topbar-left { gap: 14px; }
        }
      `}</style>

      {/* Top bar */}
      <div className="topbar">
        <div className="container">
          <div className="topbar-inner">
            <div className="topbar-left">
              <a href="tel:0469847803" className="topbar-link">
                <Phone size={12} /> 0469 847 803
              </a>
              <a href="mailto:admin@trinityconsultingservices.com.au" className="topbar-link">
                <Mail size={12} /> admin@trinityconsultingservices.com.au
              </a>
            </div>
            <div className="topbar-right">
              <Link to="/why-choose-us" className="topbar-nav-link">Why Choose Us</Link>
              <Link to="/who-we-are" className="topbar-nav-link">Who We Are</Link>
              <a href="https://www.mytcs.com.au/forms/authorization-form/" target="_blank" rel="noreferrer" className="topbar-nav-link">Forms</a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className="navbar">
        <div className="container">
          <div className="navbar-inner">
            <Link to="/" className="nav-logo">
              <img src="/logo.png" alt="Trinity Accounting Consultants" />
            </Link>

            <div className="nav-menu">
              <div className="nav-item">
                <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
              </div>
              <div className="nav-item">
                <div className="nav-link">
                  What We Do <ChevronDown size={13} />
                </div>
                <div className="nav-dropdown">
                  {serviceLinks.map(s => (
                    <Link key={s.to} to={s.to}>{s.label}</Link>
                  ))}
                  <Link to="/services" style={{fontWeight:600,color:'var(--gold)'}}>→ View All Services</Link>
                </div>
              </div>
              <div className="nav-item">
                <Link to="/why-choose-us" className={`nav-link ${isActive('/why-choose-us') ? 'active' : ''}`}>Why Choose Us</Link>
              </div>
              <div className="nav-item">
                <Link to="/who-we-are" className={`nav-link ${isActive('/who-we-are') ? 'active' : ''}`}>Who We Are</Link>
              </div>
              <div className="nav-item">
                <a href="https://www.mytcs.com.au/forms/authorization-form/" target="_blank" rel="noreferrer" className="nav-link">Forms</a>
              </div>
              <div className="nav-item">
                <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact Us</Link>
              </div>
            </div>

            <div style={{display:'flex',alignItems:'center',gap:'0'}}>
              <a href="tel:0469847803" className="nav-phone">
                <Phone size={16} /> 0469 847 803
              </a>
              <a href="https://www.mytcs.com.au/" target="_blank" rel="noreferrer" className="nav-cta-btn">
                Book Appointment
              </a>
            </div>

            <button className="mobile-btn" onClick={() => setMobileOpen(true)}>
              <Menu size={26} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div className={`mobile-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* Mobile drawer */}
      <div className={`mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <img src="/logo.png" alt="Trinity" />
          <button className="drawer-close" onClick={() => setMobileOpen(false)}><X size={22} /></button>
        </div>
        <div className="drawer-body">
          <Link to="/" className={`mob-nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
          <button className="mob-services-toggle" onClick={() => setMobileServicesOpen(!mobileServicesOpen)}>
            What We Do <ChevronDown size={15} style={{transform: mobileServicesOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.3s'}} />
          </button>
          <div className={`mob-sub ${mobileServicesOpen ? 'open' : ''}`}>
            {serviceLinks.map(s => <Link key={s.to} to={s.to}>{s.label}</Link>)}
          </div>
          <Link to="/why-choose-us" className="mob-nav-link">Why Choose Us</Link>
          <Link to="/who-we-are" className="mob-nav-link">Who We Are</Link>
          <a href="https://www.mytcs.com.au/forms/authorization-form/" target="_blank" rel="noreferrer" className="mob-nav-link">Forms</a>
          <Link to="/contact" className="mob-nav-link">Contact Us</Link>
        </div>
        <div className="drawer-footer">
          <a href="tel:0469847803" className="mob-phone-btn"><Phone size={15} /> 0469 847 803</a>
          <a href="https://www.mytcs.com.au/" target="_blank" rel="noreferrer" className="mob-book-btn"><Calendar size={15} /> Book an Appointment</a>
        </div>
      </div>
    </>
  );
}
